/**
 * Type definitions for the vouchers/documents module.
 *
 * NOTE: The PostgreSQL tables are named "documents", "documentinfo", and "documentcoverage"
 * because they store all financial document types (not just vouchers).
 *
 * For backward compatibility, we export aliases: Voucher = Document, VoucherInfo = DocumentInfo
 */

import { ClientRef } from '../ClientRef';
import { Currency } from '../currencies/types';
import { VoucherSourceTargetTypes } from '../travelFiles/constants/voucherSourceTarget';
import { TravelFileTypes } from '../travelFiles/constants/travelFileTypesOptions';
import { Phone } from '../schemas/phoneSchema';
import { Address } from '../schemas/addressSchema';
import { PaymentMethodObject } from '../../routes/Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/Receipt/PreReceiptState';
import { TravelAgencyAccount } from '../travelAgencyAccounts/types';
import { BeneficiaryItem } from '../../routes/Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/PassengerBeneficiaryFormField';
import { ServiceNote } from '../services/ServiceNote/types';
import { RemittancePaymentMethodObject } from '../../routes/Financial/generateDocumentsForms/Remittance/Remittance';
import { DocumentTypes, PaymentMethodTypes, VoucherStatuses } from './helpers';

// ----- Certification Types -----

export type voucherCertificationTypes = 'Original' | 'Copy' | 'Certified Copy' | 'Draft';
export type voucherCertificationTypesKeys = 'ORIGINAL' | 'COPY' | 'CERTIFIED_COPY';
export const voucherCertificationTypesEnum: {
    [key in voucherCertificationTypesKeys]: voucherCertificationTypes;
} = {
    ORIGINAL: 'Original',
    COPY: 'Copy',
    CERTIFIED_COPY: 'Certified Copy'
};

// ----- Subtypes -----

export type VoucherSubtypes = 'Service';

// ----- Supplier Invoice -----

export type SupplierInvoice = {
    number: number;
    issueDate: Date | string | null;
    dueDate: Date | string | null;
    vat: number;
};

// ----- Document Info (formerly VoucherInfo) -----
// NOTE: pdfInfo is excluded - it's generated at runtime only, not stored in DB
// NOTE: coveredBy/isCovering are now in the documentcoverage junction table

export type DocumentInfo = {
    _id?: string;
    idx?: number;
    documentId?: string; // FK to documents table (PostgreSQL)

    // Financial fields
    amount: number;
    amountLocal?: number;
    /** Legacy object↔doc (USD↔doc) rate — never use it to convert to local. */
    rate?: number;
    /** "1 document-currency unit = X local-currency units" (ILS per doc unit). */
    localRate?: number;
    currency: Currency | Currency['_id'] | null;
    price?: number;
    supplierPrice: number;
    sellingPrice?: number;

    // Commission & Tax
    commission?: number;
    markup?: number;
    tax?: number;
    vatOnCommission?: number;
    vatPercentage?: number;
    reduction?: number;
    netToRemit?: number;
    leftToPay?: number;
    /** Refund transaction fee ("P.Fee"), entered manually — no GL posting. */
    processingFee?: number;
    /** Airline credit / Miscellaneous Charge Order deducted from a refund. */
    creditMco?: number;

    // Document references
    subtype?: VoucherSubtypes;
    voucherNumberRefundRelated?: number;
    splitFrom?: string; // Document ID
    aci?: string; // Agent commission invoice ID
    aciRef?: string;
    supplierInvoice?: SupplierInvoice;

    // Dates
    dueDate: Date | string | null;
    serviceStartDate?: Date;
    serviceEndDate?: Date;

    // Party information
    supplierName: string;
    supplierDetails?: { phones: Phone[]; address: Address };
    travelAgentName: string;
    reservingTravelAgent: TravelAgencyAccount | string;
    accountName: string;
    travelFileNumber: string | undefined;

    // Service details
    serviceName: string;
    description?: string;
    reservationNumber: string;
    referenceNumber: string;
    agentReference?: string;

    // Payment
    paymentMethodType?: PaymentMethodTypes;
    paymentMethodObject?: PaymentMethodObject | RemittancePaymentMethodObject;
    isDeposited?: boolean;
    isWithdrawn?: boolean;
    isRepay?: boolean;
    /** The document this repay pays back (galor repayfordocnumber). A PRQ and
     *  the remittance it converts into share this — see add_repay_link_columns.sql. */
    repayForDocumentNumber?: number | null;
    repayForDocumentType?: number | null;
    isRefund?: boolean;
    /** Which Payments Terminal this document's money moved through, and the
     *  gateway terminal number as charged. A refund or repay has to go back
     *  through the same terminal (same card), so both are pinned from the
     *  source document rather than picked again. See
     *  add_document_payments_terminal.sql and resolveDocumentTerminal. */
    paymentsTerminalId?: number | null;
    paymentsTerminalNumber?: string | null;

    // Other fields
    remark?: string;
    incomeType?: string;
    for?: string;
    beneficiary?: BeneficiaryItem[];
    printReceiptEquivalentAmount?: boolean;
    printReceiptEquivalentAmountText?: string;
    printExchangeRate?: boolean;
    // Per-document toggle for the second currency column on TXI/ACI PDFs.
    // undefined/true → show; false → hide. See buildTaxInvoice/buildAgentInvoice.
    printLocalCurrency?: boolean;
    // Per-document toggle for the passengers table on Voucher/Refund PDFs, set
    // from the (inverted) "Show Passengers Table in Voucher PDF" checkbox.
    // undefined → no explicit choice, builder default applies. See buildServiceVoucher.
    hidePassengersTable?: boolean;
    rateAffectBase?: boolean;
    /** Per-passenger refund selection (servicespassengers ids) and the amount
     *  issued for each. NOT projected by DOCUMENT_INFO_JOIN — the getById route
     *  adds them for Refund documents; see buildServiceRefundPdfInfo. */
    refundPassengerIds?: Array<string | number>;
    refundPassengerDetails?: Array<{
        id: string | number;
        name?: string;
        supplierPrice?: number;
        sellingPrice?: number;
        tax?: number;
        refundNumber?: string;
    }>;

    // Coverage arrays - populated from documentcoverage junction table for API compatibility
    coveredBy?: Array<string>;
    isCovering?: Array<string>;

    // S3 PDF storage
    pdfUrl?: string | { s3Key: string };

    // Timestamps
    createdAt?: Date;
    updatedAt?: Date;
    isDeleted?: Date | null;
};

// ----- Document Coverage (junction table) -----

export interface DocumentCoverage extends Partial<ClientRef> {
    _id: string;
    idx?: number;
    coveringDocumentId: string; // The document doing the covering (e.g., Receipt)
    coveredDocumentId: string; // The document being covered (e.g., Invoice)
    createdAt?: Date;
    isDeleted?: Date | null;
}

// ----- Document Passenger -----

export interface DocumentPassenger extends Partial<ClientRef> {
    _id: string;
    idx?: number;
    documentId: string; // FK to documents table
    title?: string;
    name?: string;
    dob?: Date | string | null;
    age?: number;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    phone?: Phone | null;
    isLead?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
    isDeleted?: Date | null;
}

// ----- Document (formerly Voucher) -----

export interface Document extends ClientRef {
    _id: string;
    idx?: number; // PostgreSQL auto-increment
    number: number;
    type: DocumentTypes;
    status: VoucherStatuses;
    createdAt: Date;
    updatedAt?: Date;
    /**
     * The date the document was issued, as Galor recorded it (`document.issue_dt`).
     * Distinct from `createdAt`, which for imported rows is the Galor *entry*
     * timestamp — invoice 309971 was issued 18-May-22 and keyed in on 19-May-22.
     * The column has existed since add_documents_issuedate.sql; only the type
     * was missing, which is why every PDF fell back to printing `createdAt`.
     */
    issueDate?: Date | string | null;

    // Travel file references
    travelFileId?: string;
    travelFileType?: TravelFileTypes;
    travelFileServiceId?: string | null;

    // Source entity
    sourceId: string;
    sourceType: VoucherSourceTargetTypes;
    sourceNumber: string;

    // Target entity
    targetId: string;
    targetType: VoucherSourceTargetTypes;
    targetNumber: string;

    // Status flags
    isPaid: boolean;
    isApproved?: boolean;
    isExportedToGeneralLedger: boolean;
    isHistory: boolean;

    // Approval flow
    approvedBy?: number | null;
    approvalDate?: Date | null;
    rejectionReason?: string | null;
    approverName?: string; // joined from users table

    // Currency
    currency?: Currency | Currency['_id'] | null;

    // Notes
    notes: Array<ServiceNote>;

    // Document info (joined from documentinfo table)
    info: DocumentInfo;

    // Passenger info (joined from documentpassenger table)
    passenger?: DocumentPassenger;

    // Audit fields
    createdBy?: string;
    updatedBy?: string;

    // Soft delete
    isDeleted?: Date | null;
}

// ----- Backward Compatibility Aliases -----
// These ensure existing code continues to work

export type Voucher = Document;
export type VoucherInfo = DocumentInfo;
